import { Button } from '@sparkery/ui';
import './startup.css';

/**
 * The states that exist before the shell can render.
 *
 * Deliberately not inside the shell: the shell needs a viewer, and these are what
 * happens when there is not one. All are plain, self-contained and styled from tokens.
 */

export interface SignInRequiredProps {
  /** Starts provider sign-in, or selects the default local dev identity. */
  onSignIn?: () => void;
  /** Present only in automated test auth. */
  onSignInAsAdmin?: () => void;
  /**
   * Set when the provider DID authenticate someone the portal has no seat for.
   *
   * Naming them is the whole point: "sign in" is useless advice to somebody already
   * signed in as the wrong account, and this is the state a partner hits when their
   * organisation has not been mapped yet.
   */
  signedInAs?: string;
}

export function SignInRequired({ onSignIn, onSignInAsAdmin, signedInAs }: SignInRequiredProps) {
  const devLogin = Boolean(onSignInAsAdmin) && !signedInAs;

  return (
    <div className="startup startupLogin">
      <main className="startupLoginContent">
        <header className="startupLoginHeader">
          <img className="startupLoginLogo" src="/assets/logo-mark.svg" alt="Sparkery" />
          <h1 className="startupTitle">Partner Portal</h1>
        </header>

        <div className="startupCard">
          {devLogin && onSignIn && onSignInAsAdmin ? (
            <form
              className="startupForm"
              onSubmit={(event) => {
                event.preventDefault();
                onSignIn();
              }}
            >
              <label className="startupField">
                <span className="startupLabel">Email</span>
                <input
                  className="startupInput"
                  type="email"
                  placeholder="Your email address"
                  autoComplete="email"
                />
              </label>
              <label className="startupField">
                <span className="startupLabel">Password</span>
                <input
                  className="startupInput"
                  type="password"
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </label>
              <Button type="submit" variant="primary" fullWidth shape="rounded">
                Continue with email
              </Button>
              <Button variant="ghostDark" fullWidth shape="rounded" onClick={onSignInAsAdmin}>
                Admin sign in
              </Button>
            </form>
          ) : signedInAs ? (
            <>
              <p className="startupBody">
                You are signed in as <strong className="startupBodyStrong">{signedInAs}</strong>,
                but that account has no access to the partner portal.
              </p>
              <p className="startupHint">
                If you expected access, ask Sparkery to link your organisation to a partner account.
              </p>
            </>
          ) : (
            <p className="startupBody">Continue to Sparkery&rsquo;s secure sign-in.</p>
          )}

          {!devLogin && onSignIn ? (
            <div className="startupActions">
              <Button onClick={onSignIn} variant="primary" fullWidth shape="rounded">
                {signedInAs ? 'Sign in as someone else' : 'Sign In'}
              </Button>
              {onSignInAsAdmin ? (
                <Button variant="ghostDark" fullWidth shape="rounded" onClick={onSignInAsAdmin}>
                  Sign in as admin
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="startupHint">
              No identity provider is configured for this build, so the API&rsquo;s development
              provider issues the session. Seeing this means the API is reachable but returned no
              viewer.
            </p>
          )}
        </div>

        <p className="startupFooter">
          Don&rsquo;t have an account?{' '}
          <a
            className="startupFooterLink"
            href="https://www.sparkery.com"
            target="_blank"
            rel="noreferrer"
          >
            Contact Sparkery
          </a>
        </p>
      </main>
    </div>
  );
}

/**
 * Shown while the provider resolves a session and the viewer is fetched.
 *
 * A blank page for the same period reads as a broken app, and this is a real wait: the
 * redirect back from AuthKit lands here before anything can render.
 */
export function StartingUp() {
  return (
    <div className="startup startupLogin">
      <main className="startupLoginContent">
        <header className="startupLoginHeader">
          <img className="startupLoginLogo" src="/assets/logo-mark.svg" alt="Sparkery" />
          <h1 className="startupTitle">Sign in</h1>
        </header>

        <div className="startupCard">
          <p className="startupBody" role="status">
            Signing you in…
          </p>
        </div>
      </main>
    </div>
  );
}

export function StartupError({ message }: { message: string }) {
  return (
    <div className="startup startupLogin">
      <main className="startupLoginContent">
        <header className="startupLoginHeader">
          <img className="startupLoginLogo" src="/assets/logo-mark.svg" alt="Sparkery" />
          <h1 className="startupTitle">Sign in</h1>
        </header>

        <div className="startupCard">
          <h2 className="startupCardTitle">The portal could not start</h2>
          <p className="startupBody">
            Please try again later, or contact support if the problem persists.
          </p>
          <p className="startupHint">{message}</p>
        </div>
      </main>
    </div>
  );
}
